import { z } from "zod";

import { pageRefSchema, provenanceTypeSchema, reviewStateSchema } from "../schema";

const sceneIdSchema = z.enum(["M3S1", "M3S2", "M3S3", "M3S4"]);
const taskIdSchema = z.enum(["T08", "T09", "T10"]);
const nodeIdSchema = z.string().regex(/^M3S[1-4]_N0[1-5]$/);
const claimIdSchema = z.string().regex(/^M3S[1-4]_CLAIM_[A-Z0-9_]+$/);

const sourceAttributionSchema = z.object({
  sourceRefs: z.tuple([z.literal("SRC_TEXTBOOK_CH3")]),
  provenanceType: provenanceTypeSchema,
  sourceCategory: z.literal("教材正文"),
  citationLabel: z.literal("《中国共产党简史》第三章"),
  pageRefs: z.array(pageRefSchema).length(1),
});

const assetSuggestionSchema = z.object({
  id: z.string().regex(/^AST-M3S[1-4]-SUG-[0-9]{3}$/),
  kind: z.enum(["programmatic_2_5d", "programmatic_3d", "interface", "archive_media"]),
  label: z.string().min(1),
  purpose: z.string().min(1),
  representationLabel: z.enum([
    "复原性创作·非档案影像",
    "教材载述史料·非档案原件",
    "档案素材建议·待来源与权利核验",
  ]),
  rightsPlan: z.enum(["original_procedural", "pending_source_and_rights_review"]),
});

const interactionSuggestionSchema = z.object({
  mode: z.enum([
    "guided_timeline",
    "policy_comparison",
    "document_evidence_reader",
    "regional_evidence_map",
    "evidence_matrix",
    "statistical_ledger",
    "solemn_archive_witness",
    "hidden_network_map",
    "rescue_route_reader",
  ]),
  learnerAction: z.string().min(1),
  feedback: z.string().min(1),
  nonScored: z.literal(true),
  historyConstraint: z.literal("只核验既定史实，不改变历史结果"),
});

const renderSchema = z.object({
  kind: z.enum([
    "situation_timeline",
    "policy_shift_matrix",
    "document_wall",
    "regional_evidence_map",
    "statistical_ledger",
    "solemn_archive_witness",
    "resistance_network",
    "rescue_route",
    "principle_matrix",
  ]),
  visualSummary: z.string().min(1),
  interaction: interactionSuggestionSchema,
  guardrails: z.array(z.string().min(1)).min(1),
});

const verbatimQuoteSchema = z.object({
  text: z.string().min(1),
  context: z.string().min(1),
  verbatim: z.literal(true),
});

export const m3BlueprintNodeSchema = z
  .object({
    id: nodeIdSchema,
    sceneId: sceneIdSchema,
    order: z.number().int().positive(),
    nodeType: z.enum(["lecture", "archive", "witness"]),
    title: z.string().min(1),
    timeAnchor: z.string().min(1),
    estimatedSeconds: z.number().int().positive(),
    claimId: claimIdSchema,
    claim: z.string().min(1),
    evidencePoints: z.array(z.string().min(1)).min(1),
    verbatimQuotes: z.array(verbatimQuoteSchema),
    assetSuggestions: z.array(assetSuggestionSchema).min(1),
    source: sourceAttributionSchema,
    sensitivity: z.enum(["standard", "trauma_witness"]),
    render: renderSchema,
    reviewState: reviewStateSchema,
    reviewNote: z.string().min(1).optional(),
  })
  .superRefine((node, context) => {
    if (!node.id.startsWith(`${node.sceneId}_`)) {
      context.addIssue({
        code: "custom",
        message: `${node.id}必须归属于其sceneId。`,
        path: ["id"],
      });
    }
    if (!node.claimId.startsWith(`${node.sceneId}_`)) {
      context.addIssue({
        code: "custom",
        message: `${node.claimId}必须归属于其sceneId。`,
        path: ["claimId"],
      });
    }
    if (
      node.source.provenanceType !== "textbook" ||
      node.source.sourceRefs[0] !== "SRC_TEXTBOOK_CH3"
    ) {
      context.addIssue({
        code: "custom",
        message: `${node.id}必须明确归属教材第三章。`,
        path: ["source"],
      });
    }
    if (node.sensitivity === "trauma_witness") {
      const requiredGuardrails = [
        "采用庄重史料见证",
        "不作第一人称受难重演",
        "不呈现猎奇或伤害特写",
        "不设置战果积分或庆祝动画",
        "不使用失败重来叙事",
      ];
      if (node.nodeType !== "witness" || node.render.kind !== "solemn_archive_witness") {
        context.addIssue({
          code: "custom",
          message: `${node.id}的创伤内容必须使用庄重史料见证节点。`,
          path: ["render"],
        });
      }
      for (const guardrail of requiredGuardrails) {
        if (!node.render.guardrails.includes(guardrail)) {
          context.addIssue({
            code: "custom",
            message: `${node.id}缺少创伤史料呈现底线：${guardrail}。`,
            path: ["render", "guardrails"],
          });
        }
      }
    }
  });

const sceneReviewNoteSchema = z.object({
  id: z.string().regex(/^M3S[1-4]_REVIEW_[A-Z0-9_]+$/),
  scope: z.string().min(1),
  state: z.literal("open"),
  note: z.string().min(1),
});

export const m3SceneBlueprintSchema = z
  .object({
    id: sceneIdSchema,
    order: z.number().int().min(1).max(4),
    title: z.string().min(1),
    identity: z.literal("抗战史料研学员"),
    timeLabel: z.string().min(1),
    mainVenue: z.string().min(1),
    archiveSlices: z.array(z.string().min(1)),
    pageRefs: z.array(pageRefSchema).length(1),
    lectureBudgetSeconds: z.number().int().positive(),
    formalTaskIds: z.array(taskIdSchema).max(1),
    taskAnchorNodeId: nodeIdSchema.optional(),
    sequence: z.array(z.string().regex(/^(M3S[1-4]_N0[1-5]|T0[89]|T10)$/)).min(3),
    localExtensionIds: z.array(z.string()).length(0),
    nodes: z.array(m3BlueprintNodeSchema).min(3).max(5),
    reviewState: reviewStateSchema,
    reviewNotes: z.array(sceneReviewNoteSchema),
  })
  .superRefine((scene, context) => {
    const nodeIds = scene.nodes.map((node) => node.id);
    const claimIds = scene.nodes.map((node) => node.claimId);
    const expectedSequence = [...nodeIds, ...scene.formalTaskIds];

    if (scene.nodes.some((node) => node.sceneId !== scene.id)) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}只能包含本场景节点。`,
        path: ["nodes"],
      });
    }
    if (new Set(nodeIds).size !== nodeIds.length || new Set(claimIds).size !== claimIds.length) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}节点ID和claimId必须唯一。`,
        path: ["nodes"],
      });
    }
    if (scene.nodes.some((node, index) => node.order !== (index + 1) * 10)) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}节点必须按10递增声明。`,
        path: ["nodes"],
      });
    }
    if (
      new Set(scene.sequence).size !== scene.sequence.length ||
      scene.sequence.length !== expectedSequence.length ||
      expectedSequence.some((item) => !scene.sequence.includes(item))
    ) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}序列必须且只能完整包含本场景节点和正式任务。`,
        path: ["sequence"],
      });
    }
    if (
      scene.nodes.reduce((total, node) => total + node.estimatedSeconds, 0) !==
      scene.lectureBudgetSeconds
    ) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}节点时长合计必须等于场景讲授预算。`,
        path: ["lectureBudgetSeconds"],
      });
    }
    if (scene.formalTaskIds.length === 0 && scene.taskAnchorNodeId !== undefined) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}没有正式任务，不得配置任务锚点。`,
        path: ["taskAnchorNodeId"],
      });
    }
    if (
      scene.formalTaskIds.length === 1 &&
      (!scene.taskAnchorNodeId || !nodeIds.includes(scene.taskAnchorNodeId))
    ) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}正式任务必须锚定本场景内容节点。`,
        path: ["taskAnchorNodeId"],
      });
    }
  });

const moduleReviewNoteSchema = z.object({
  id: z.string().regex(/^M3_REVIEW_[A-Z0-9_]+$/),
  scope: z.string().min(1),
  state: z.literal("open"),
  note: z.string().min(1),
});

export const m3BlueprintSchema = z
  .object({
    id: z.literal("M3"),
    version: z.literal("1.0.0"),
    title: z.literal("坚持抗战、团结、进步的方针"),
    identity: z.literal("抗战史料研学员"),
    printPageRange: z.literal("84—91"),
    pdfPageRange: z.literal("104—111"),
    sourceRefs: z.tuple([z.literal("SRC_TEXTBOOK_CH3")]),
    taskIds: z.tuple([z.literal("T08"), z.literal("T09"), z.literal("T10")]),
    scenes: z.array(m3SceneBlueprintSchema).length(4),
    reviewState: reviewStateSchema,
    reviewNotes: z.array(moduleReviewNoteSchema).min(1),
  })
  .superRefine((blueprint, context) => {
    const expectedSceneIds = ["M3S1", "M3S2", "M3S3", "M3S4"];
    const expectedTaskMapping: Readonly<Record<string, readonly string[]>> = {
      M3S1: ["T08"],
      M3S2: ["T09"],
      M3S3: [],
      M3S4: ["T10"],
    };

    if (
      blueprint.scenes.some(
        (scene, index) => scene.id !== expectedSceneIds[index] || scene.order !== index + 1,
      )
    ) {
      context.addIssue({
        code: "custom",
        message: "模块三必须固定保留M3S1—M3S4四场景及其顺序。",
        path: ["scenes"],
      });
    }
    for (const [index, scene] of blueprint.scenes.entries()) {
      const expectedTasks = expectedTaskMapping[scene.id] ?? [];
      if (JSON.stringify(scene.formalTaskIds) !== JSON.stringify(expectedTasks)) {
        context.addIssue({
          code: "custom",
          message: `${scene.id}正式任务映射不得改变。`,
          path: ["scenes", index, "formalTaskIds"],
        });
      }
    }
  });

export type M3BlueprintNode = z.infer<typeof m3BlueprintNodeSchema>;
export type M3SceneBlueprint = z.infer<typeof m3SceneBlueprintSchema>;
export type M3Blueprint = z.infer<typeof m3BlueprintSchema>;

const textbookSource = (print: string, pdf: string): M3BlueprintNode["source"] => ({
  sourceRefs: ["SRC_TEXTBOOK_CH3"],
  provenanceType: "textbook",
  sourceCategory: "教材正文",
  citationLabel: "《中国共产党简史》第三章",
  pageRefs: [{ print, pdf }],
});

const standardGuardrails: string[] = ["不模拟学生改变历史结果", "不使用战果积分或娱乐化庆祝"];
const traumaGuardrails: string[] = [
  "采用庄重史料见证",
  "不作第一人称受难重演",
  "不呈现猎奇或伤害特写",
  "不设置战果积分或庆祝动画",
  "不使用失败重来叙事",
];

export const m3Blueprint = {
  id: "M3",
  version: "1.0.0",
  title: "坚持抗战、团结、进步的方针",
  identity: "抗战史料研学员",
  printPageRange: "84—91",
  pdfPageRange: "104—111",
  sourceRefs: ["SRC_TEXTBOOK_CH3"],
  taskIds: ["T08", "T09", "T10"],
  scenes: [
    {
      id: "M3S1",
      order: 1,
      title: "战略相持阶段到来后的局势和党的方针",
      identity: "抗战史料研学员",
      timeLabel: "1938年10月—1939年7月7日",
      mainVenue: "武汉、广州、延安等关键节点构成的全国抗战局势场景",
      archiveSlices: [],
      pageRefs: [{ print: "84", pdf: "104" }],
      lectureBudgetSeconds: 180,
      formalTaskIds: ["T08"],
      taskAnchorNodeId: "M3S1_N03",
      sequence: ["M3S1_N01", "M3S1_N02", "M3S1_N03", "T08"],
      localExtensionIds: [],
      nodes: [
        {
          id: "M3S1_N01",
          sceneId: "M3S1",
          order: 10,
          nodeType: "lecture",
          title: "战略相持阶段到来",
          timeAnchor: "1938年10月",
          estimatedSeconds: 50,
          claimId: "M3S1_CLAIM_STALEMATE_BEGINS",
          claim:
            "日军占领广州、武汉后已无力再发动大规模战略进攻，全民族抗战由战略防御阶段进入战略相持阶段。",
          evidencePoints: [
            "1938年10月，日军占领广州、武汉。",
            "日军已无力再发动大规模的战略进攻。",
            "全民族抗日战争由战略防御阶段进入战略相持阶段。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M3S1-SUG-001",
              kind: "programmatic_2_5d",
              label: "广州—武汉—延安局势时间线",
              purpose: "用分时节点区分城市陷落、阶段转换与党的判断，不把多地拼成同一现场。",
              representationLabel: "复原性创作·非档案影像",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("84", "104"),
          sensitivity: "standard",
          render: {
            kind: "situation_timeline",
            visualSummary: "全国地图只显示经教材确认的三个时空节点和阶段转换箭头。",
            interaction: {
              mode: "guided_timeline",
              learnerAction: "依次打开广州、武汉和延安节点，核对事件与判断的先后关系。",
              feedback: "完成后归纳“城市陷落不等于抗战失败，而是战争进入战略相持阶段”。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M3S1_N02",
          sceneId: "M3S1",
          order: 20,
          nodeType: "lecture",
          title: "战略相持阶段到来后的局势",
          timeAnchor: "1938年10月—1939年",
          estimatedSeconds: 70,
          claimId: "M3S1_CLAIM_POLICY_SHIFT_AND_CRISIS",
          claim:
            "日本侵略者调整侵华策略，国民党统治集团内的投降、分裂、倒退活动随之加剧，团结抗战面临严重危机。",
          evidencePoints: [
            "日军逐渐将主要兵力用于打击敌后战场的八路军和新四军。",
            "对国民政府由军事进攻为主、政治诱降为辅，转为政治诱降为主、军事打击为辅。",
            "在占领区加紧扶植傀儡政权，建立和发展汉奸组织。",
            "1938年12月，汪精卫为代表的国民党亲日派公开投降；团结抗战局面出现严重危机。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M3S1-SUG-002",
              kind: "interface",
              label: "侵华策略转变对照矩阵",
              purpose: "比较敌后战场、国民政府和占领区三个方向的策略变化及其后果。",
              representationLabel: "教材载述史料·非档案原件",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("84", "104"),
          sensitivity: "standard",
          render: {
            kind: "policy_shift_matrix",
            visualSummary: "三列政策变化卡与一条团结危机因果链，避免把复杂局势简化为单一战场动画。",
            interaction: {
              mode: "policy_comparison",
              learnerAction: "逐列翻阅调整前后表述，再把对应影响连接到团结危机证据。",
              feedback: "系统说明每项策略如何加剧投降、分裂和倒退风险。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M3S1_N03",
          sceneId: "M3S1",
          order: 30,
          nodeType: "archive",
          title: "《为抗战两周年纪念对时局宣言》",
          timeAnchor: "1939年7月7日",
          estimatedSeconds: 60,
          claimId: "M3S1_CLAIM_THREE_SLOGANS",
          claim: "在全民族抗日战争的关键时刻，中国共产党举起了团结抗战到底的鲜明旗帜。",
          evidencePoints: [
            "1939年7月7日，党中央发出《为抗战两周年纪念对时局宣言》。",
            "三句口号分别回应中途妥协、内部分裂和向后倒退。",
          ],
          verbatimQuotes: [
            {
              text: "坚持抗战到底——反对中途妥协！巩固国内团结——反对内部分裂！力求全国进步——反对向后倒退！",
              context: "《为抗战两周年纪念对时局宣言》三句口号",
              verbatim: true,
            },
          ],
          assetSuggestions: [
            {
              id: "AST-M3S1-SUG-003",
              kind: "interface",
              label: "《对时局宣言》三联文献墙",
              purpose: "将三句原文并列展示并提供逐句出处，不模拟档案原件。",
              representationLabel: "教材载述史料·非档案原件",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("84", "104"),
          sensitivity: "standard",
          render: {
            kind: "document_wall",
            visualSummary: "三联文献卡以原文、所反对倾向和统一战线意义构成可核验关系。",
            interaction: {
              mode: "document_evidence_reader",
              learnerAction: "逐句展开原文并标记“抗战—团结—进步”与对应反对项。",
              feedback: "核对完成后开放正式任务T08，答错返回本节点而非播放失败动画。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
      ],
      reviewState: "pending_review",
      reviewNotes: [],
    },
    {
      id: "M3S2",
      order: 2,
      title: "敌后游击战争的发展和百团大战",
      identity: "抗战史料研学员",
      timeLabel: "1939年—1941年1月，重点为1940年8月—1941年1月",
      mainVenue: "华北敌后战场及主要铁路、公路交通线",
      archiveSlices: [],
      pageRefs: [{ print: "85—87", pdf: "105—107" }],
      lectureBudgetSeconds: 240,
      formalTaskIds: ["T09"],
      taskAnchorNodeId: "M3S2_N04",
      sequence: ["M3S2_N01", "M3S2_N02", "M3S2_N03", "M3S2_N04", "M3S2_N05", "T09"],
      localExtensionIds: [],
      nodes: [
        {
          id: "M3S2_N01",
          sceneId: "M3S2",
          order: 10,
          nodeType: "lecture",
          title: "敌后游击战争成为主要对日作战方式",
          timeAnchor: "1939—1940年",
          estimatedSeconds: 45,
          claimId: "M3S2_CLAIM_MAIN_RESPONSIBILITY",
          claim:
            "战略相持阶段，中国共产党肩负起抗击日本侵略军的主要责任，党领导的敌后游击战争成为主要对日作战方式。",
          evidencePoints: [
            "日军对抗日根据地“扫荡”的重点是华北地区。",
            "1939年和1940年，仅华北地区日军出动千人以上的大规模“扫荡”就有109次。",
            "上述大规模“扫荡”使用兵力总计在50万人以上。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M3S2-SUG-001",
              kind: "programmatic_2_5d",
              label: "华北敌后“扫荡”压力证据图",
              purpose: "按年度和区域读取109次、50万人以上等教材统计，不模拟作战指挥。",
              representationLabel: "复原性创作·非档案影像",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("85", "105"),
          sensitivity: "standard",
          render: {
            kind: "regional_evidence_map",
            visualSummary: "华北区域压力热度与教材统计卡共同呈现，不显示击杀积分。",
            interaction: {
              mode: "regional_evidence_map",
              learnerAction: "选择1939或1940年度，读取华北敌后作战压力的教材统计证据。",
              feedback: "把统计与“主要责任、主要对日作战方式”结论建立证据联系。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M3S2_N02",
          sceneId: "M3S2",
          order: 20,
          nodeType: "archive",
          title: "华北、华中、华南、东北敌后抗战发展",
          timeAnchor: "1938年冬—1940年底",
          estimatedSeconds: 60,
          claimId: "M3S2_CLAIM_REGIONAL_DEVELOPMENT",
          claim: "党领导的敌后抗战在华北、华中、华南和东北持续发展，人民抗日力量在战斗中壮大。",
          evidencePoints: [
            "1939年11月黄土岭伏击战击毙日军所谓“名将之花”阿部规秀中将。",
            "1939年2月确定新四军向南巩固、向东作战、向北发展；1940年5月党中央派八路军一部1.2万人南下。",
            "广东党组织领导创建东江抗日游击根据地和东江纵队，海南岛红军游击队后来发展为琼崖纵队。",
            "东北抗日联军长期坚持白山黑水斗争；冷云等8名女战士和杨靖宇壮烈牺牲。",
            "到1940年底，共产党领导的武装部队发展到50万人，东北抗日联军未计算在内；创建16块抗日民主根据地。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M3S2-SUG-002",
              kind: "interface",
              label: "四区域敌后抗战证据矩阵",
              purpose: "分区读取时间、组织和发展证据，避免把多地事件拼接成同一战场。",
              representationLabel: "教材载述史料·非档案原件",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("85—86", "105—106"),
          sensitivity: "standard",
          render: {
            kind: "regional_evidence_map",
            visualSummary: "四区域地图与证据抽屉共同呈现，各区域保留独立时间标记。",
            interaction: {
              mode: "evidence_matrix",
              learnerAction: "按华北、华中、华南、东北逐区展开史料，完成区域—组织—时间对应。",
              feedback: "系统强调敌后抗战的全国性发展及各地时空差异。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M3S2_N03",
          sceneId: "M3S2",
          order: 30,
          nodeType: "archive",
          title: "国际友人参加敌后抗战",
          timeAnchor: "1939年11—12月",
          estimatedSeconds: 35,
          claimId: "M3S2_CLAIM_INTERNATIONAL_SUPPORT",
          claim: "多位国际友人来到中国参加敌后抗战，白求恩为抢救八路军伤员献出生命。",
          evidencePoints: [
            "教材列举加拿大共产党员诺尔曼·白求恩、德国医学博士汉斯·米勒、美国医学博士马海德、印度医生柯棣华等国际友人。",
            "1939年11月，白求恩在抢救八路军伤员时感染中毒，为中国人民的解放事业献出生命。",
          ],
          verbatimQuotes: [
            {
              text: "一个高尚的人，一个纯粹的人，一个有道德的人，一个脱离了低级趣味的人，一个有益于人民的人。",
              context: "毛泽东《纪念白求恩》号召学习白求恩精神",
              verbatim: true,
            },
          ],
          assetSuggestions: [
            {
              id: "AST-M3S2-SUG-003",
              kind: "archive_media",
              label: "国际友人文献与人物资料卡",
              purpose: "建议使用经来源和权利核验的人物照片或公版文献；未核验前使用文字证据卡。",
              representationLabel: "档案素材建议·待来源与权利核验",
              rightsPlan: "pending_source_and_rights_review",
            },
          ],
          source: textbookSource("86", "106"),
          sensitivity: "standard",
          render: {
            kind: "document_wall",
            visualSummary: "以人物资料卡、时间线和教材引文呈现国际支援，不虚构人物对白。",
            interaction: {
              mode: "document_evidence_reader",
              learnerAction: "打开人物卡核对国籍、专业与参加中国抗战的教材记载。",
              feedback: "归纳中国抗战同世界反法西斯力量的联系。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M3S2_N04",
          sceneId: "M3S2",
          order: 40,
          nodeType: "archive",
          title: "百团大战发起与参战规模",
          timeAnchor: "1940年8月20日—1941年1月",
          estimatedSeconds: 45,
          claimId: "M3S2_CLAIM_HUNDRED_REGIMENTS_SCALE",
          claim: "八路军总部在华北发动大规模对日军进攻，陆续参战部队达到105个团20余万人。",
          evidencePoints: [
            "1940年8月20日夜，参战部队、游击队、民兵同时发起攻击。",
            "随着战役展开，陆续参战的部队达到105个团20余万人。",
            "百团大战是全民族抗战以来八路军在华北发动的规模最大、持续时间最长的一次带战略性进攻的战役。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M3S2-SUG-004",
              kind: "programmatic_3d",
              label: "华北交通线破袭证据研判台",
              purpose: "以铁路、公路节点和时间推进呈现战役范围；学习者只读取史料，不扮演指挥员。",
              representationLabel: "复原性创作·非档案影像",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("87", "107"),
          sensitivity: "standard",
          render: {
            kind: "regional_evidence_map",
            visualSummary: "华北主要交通线、8月20日节点和105个团20余万人统计卡同步出现。",
            interaction: {
              mode: "regional_evidence_map",
              learnerAction: "沿时间线检查发起节点、参战力量和战役性质三类证据。",
              feedback: "核对完成后开放T09下拉单选，答错回看105个团证据卡。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M3S2_N05",
          sceneId: "M3S2",
          order: 50,
          nodeType: "lecture",
          title: "百团大战战果与历史影响",
          timeAnchor: "截至1940年12月初",
          estimatedSeconds: 55,
          claimId: "M3S2_CLAIM_HUNDRED_REGIMENTS_IMPACT",
          claim:
            "百团大战沉重打击日军“囚笼政策”，提高共产党和八路军威望，并在抗日局面比较低沉时振奋全国人民信心。",
          evidencePoints: [
            "敌后军民共作战1824次，毙伤日伪军2.5万余人。",
            "俘日军281人、伪军1.8万余人。",
            "破坏铁路470余公里、公路1500余公里，并摧毁大量敌碉堡和据点。",
            "百团大战给日军“囚笼政策”以沉重打击，提高共产党和八路军的威望。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M3S2-SUG-005",
              kind: "interface",
              label: "百团大战教材统计台账",
              purpose: "以来源可见的统计分组呈现作战次数、人员与交通线数据，避免游戏化战果榜。",
              representationLabel: "教材载述史料·非档案原件",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("87", "107"),
          sensitivity: "standard",
          render: {
            kind: "statistical_ledger",
            visualSummary: "统计台账按作战、人员、交通线和影响四组呈现，每组均标明教材页码。",
            interaction: {
              mode: "statistical_ledger",
              learnerAction: "逐组展开统计并选择其能够支持的历史影响结论。",
              feedback: "强调数字用于证成历史影响，不转换为积分、排名或庆祝动画。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
      ],
      reviewState: "pending_review",
      reviewNotes: [
        {
          id: "M3S2_REVIEW_T09_PRESENTATION",
          scope: "T09",
          state: "open",
          note: "原始方案把任务9标作填空，但确认后的正式题库已按老板限定改为下拉单选；本蓝图只引用现有T09，不改题干、选项、答案或presentation=select。",
        },
      ],
    },
    {
      id: "M3S3",
      order: 3,
      title: "沦陷区人民的抗日斗争",
      identity: "抗战史料研学员",
      timeLabel: "1937—1945年，重点为1940年下半年—1942年中",
      mainVenue: "中国各沦陷区",
      archiveSlices: ["香港—东江秘密营救路线"],
      pageRefs: [{ print: "87—89", pdf: "107—109" }],
      lectureBudgetSeconds: 120,
      formalTaskIds: [],
      sequence: ["M3S3_N01", "M3S3_N02", "M3S3_N03"],
      localExtensionIds: [],
      nodes: [
        {
          id: "M3S3_N01",
          sceneId: "M3S3",
          order: 10,
          nodeType: "witness",
          title: "日本侵略者在沦陷区的殖民统治罪行",
          timeAnchor: "1940年下半年起",
          estimatedSeconds: 45,
          claimId: "M3S3_CLAIM_OCCUPATION_ATROCITIES",
          claim:
            "日军在沦陷区实行残暴殖民统治，实施细菌战、毒气战，掳掠残害劳工，并强迫中国妇女充当“慰安妇”。",
          evidencePoints: [
            "日军细菌战部队制造霍乱、伤寒、鼠疫等病毒，对中国军民实行惨无人道的“活体解剖”。",
            "日军制造和配备化学武器，实行细菌战、毒气战。",
            "从1940年下半年起，日军731部队等将带有病毒的投掷器投放到中国许多地区，造成大量居民死亡。",
            "日军掳掠和残害中国劳工，强迫中国妇女充当“慰安妇”。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M3S3-SUG-001",
              kind: "interface",
              label: "沦陷区罪行庄重史料见证墙",
              purpose:
                "以教材文字、时间地图和非具象证据目录见证细菌战、毒气战、劳工与“慰安妇”罪行，不复原伤害场面。",
              representationLabel: "教材载述史料·非档案原件",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("87—88", "107—108"),
          sensitivity: "trauma_witness",
          render: {
            kind: "solemn_archive_witness",
            visualSummary: "进入前提供内容提示；见证墙仅呈现日期、地点范围、罪行类别和教材出处。",
            interaction: {
              mode: "solemn_archive_witness",
              learnerAction: "自主展开经分级的证据目录，阅读后完成非计分的尊严导向知识归纳。",
              feedback: "只确认所读证据与侵略罪行类别的关系，不评价受害者、不播放刺激性反馈。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: traumaGuardrails,
          },
          reviewState: "pending_review",
          reviewNote:
            "正式呈现须经党史/思政、创伤知情设计和素材权利联合审核；不得以公开网络图片可得为由跳过审核。",
        },
        {
          id: "M3S3_N02",
          sceneId: "M3S3",
          order: 20,
          nodeType: "lecture",
          title: "沦陷区党组织发动群众与建立隐蔽战线",
          timeAnchor: "1937—1945年",
          estimatedSeconds: 35,
          claimId: "M3S3_CLAIM_RESISTANCE_NETWORK",
          claim:
            "沦陷区党组织在极端困难条件下开展抗日宣传、发动组织群众、举行武装暴动并建立隐蔽战线。",
          evidencePoints: [
            "党组织开展抗日宣传，启发群众民族意识，激励群众抗日热情。",
            "积极发动组织群众，举行武装暴动，建立隐蔽战线并开展情报工作。",
            "1941年大同煤矿工人举行罢工和暴动，一部分工人参加八路军。",
            "中共晋察冀分局社会部派人到北平、天津等城市联络秘密党员和爱国人士，渗透敌人内部获取情报。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M3S3-SUG-002",
              kind: "programmatic_2_5d",
              label: "沦陷区抗日宣传与隐蔽战线关系图",
              purpose: "以抽象关系节点呈现宣传、组织、武装斗争和情报工作，不展示真实秘密身份。",
              representationLabel: "复原性创作·非档案影像",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("88", "108"),
          sensitivity: "standard",
          render: {
            kind: "resistance_network",
            visualSummary: "四类斗争方式由教材证据卡连接，隐蔽线只显示抽象节点。",
            interaction: {
              mode: "hidden_network_map",
              learnerAction: "展开四类斗争方式并核对各自教材事例，不推演地下工作行动。",
              feedback: "归纳沦陷区人民抗日斗争的广泛性、多样性与艰巨性。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M3S3_N03",
          sceneId: "M3S3",
          order: 30,
          nodeType: "archive",
          title: "香港秘密大营救与东江游击力量",
          timeAnchor: "1941年太平洋战争爆发后—1942年中",
          estimatedSeconds: 40,
          claimId: "M3S3_CLAIM_HONG_KONG_RESCUE",
          claim: "党中央紧急组织香港秘密大营救，历时半年多营救爱国民主人士和文化界人士800余人。",
          evidencePoints: [
            "太平洋战争爆发后日军占领香港，党中央及周恩来紧急指示八路军驻香港办事处和东江抗日游击队负责人组织营救。",
            "营救活动历时半年多，共营救出何香凝、柳亚子、邹韬奋等800余人。",
            "香港沦陷后，广东人民抗日游击总队港九大队挺进敌后，开展海上游击战和城市游击战。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M3S3-SUG-003",
              kind: "programmatic_2_5d",
              label: "香港—东江秘密营救档案路线",
              purpose: "以分段、非精确坐标的档案关系线连接组织节点；不暴露或虚构具体秘密路线。",
              representationLabel: "复原性创作·非档案影像",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("88—89", "108—109"),
          sensitivity: "standard",
          render: {
            kind: "rescue_route",
            visualSummary: "香港、八路军驻香港办事处、东江游击力量和获救群体以档案关系节点呈现。",
            interaction: {
              mode: "rescue_route_reader",
              learnerAction: "按时间打开指示、组织、营救和后续游击行动四类史料节点。",
              feedback: "强调路线仅为关系示意，并归纳党组织在危急条件下的组织作用。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
      ],
      reviewState: "pending_review",
      reviewNotes: [
        {
          id: "M3S3_REVIEW_TRAUMA_AND_RIGHTS",
          scope: "M3S3_N01",
          state: "open",
          note: "细菌战、活体解剖、劳工与“慰安妇”内容只采用庄重史料见证；任何历史照片、档案或视频均须完成来源、权利与创伤知情审核后才可接入。",
        },
      ],
    },
    {
      id: "M3S4",
      order: 4,
      title: "打退和制止国民党顽固派的反共高潮",
      identity: "抗战史料研学员",
      timeLabel: "1939年冬—1943年，重点为1941年1—3月",
      mainVenue: "陕甘宁边区、华北、皖南和重庆",
      archiveSlices: ["皖南事变与政治舆论反击"],
      pageRefs: [{ print: "89—91", pdf: "109—111" }],
      lectureBudgetSeconds: 180,
      formalTaskIds: ["T10"],
      taskAnchorNodeId: "M3S4_N03",
      sequence: ["M3S4_N01", "M3S4_N02", "M3S4_N03", "M3S4_N04", "M3S4_N05", "T10"],
      localExtensionIds: [],
      nodes: [
        {
          id: "M3S4_N01",
          sceneId: "M3S4",
          order: 10,
          nodeType: "lecture",
          title: "维护统一战线的策略与原则",
          timeAnchor: "1939年冬—1940年春",
          estimatedSeconds: 35,
          claimId: "M3S4_CLAIM_UNITY_STRATEGY",
          claim:
            "中国共产党妥善处理民族矛盾和阶级矛盾，以策略方针和有理、有利、有节原则维护抗日民族统一战线。",
          evidencePoints: [
            "民族矛盾关系中国存亡，阶级矛盾关系能否将全民族抗战坚持到底并在战后建设新中国。",
            "为坚持、巩固和扩大统一战线，制定“发展进步势力，争取中间势力，孤立顽固势力”的策略方针。",
            "坚持“人不犯我，我不犯人，人若犯我，我必犯人”的自卫立场和“有理、有利、有节”的原则。",
            "1939年冬至1940年春，国民党顽固派掀起第一次反共高潮，中国共产党给予坚决回击。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M3S4-SUG-001",
              kind: "interface",
              label: "统一战线策略与原则矩阵",
              purpose: "把两类矛盾、三层策略和有理有利有节原则组织成证据关系。",
              representationLabel: "教材载述史料·非档案原件",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("89", "109"),
          sensitivity: "standard",
          render: {
            kind: "principle_matrix",
            visualSummary: "两类矛盾位于上层，策略方针和自卫原则位于下层，避免简单阵营化。",
            interaction: {
              mode: "policy_comparison",
              learnerAction: "将策略与其所维护的统一战线目标建立联系，并查看教材依据。",
              feedback: "说明坚持原则斗争与维护抗日大局并不矛盾。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M3S4_N02",
          sceneId: "M3S4",
          order: 20,
          nodeType: "witness",
          title: "皖南事变庄重史料见证",
          timeAnchor: "1941年1月",
          estimatedSeconds: 45,
          claimId: "M3S4_CLAIM_WANNAN_INCIDENT",
          claim: "国民党顽固派制造皖南事变，新四军皖南部队遭受重大损失，团结抗战局面受到严重破坏。",
          evidencePoints: [
            "新四军军部及所属皖南部队9000余人奉命北移途中，遭国民党军8万余人伏击和围攻。",
            "除2000余人突围外，一部被打散，大部壮烈牺牲或被俘。",
            "军长叶挺谈判时被扣押，副军长项英在突围过程中遇害。",
            "事变发生后，蒋介石诬称新四军“叛变”，宣布取消其番号。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M3S4-SUG-002",
              kind: "interface",
              label: "皖南事变庄重史料见证墙",
              purpose: "以命令、时间线、人数关系和纪念性文字呈现，不复原伏击或牺牲画面。",
              representationLabel: "教材载述史料·非档案原件",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("89—90", "109—110"),
          sensitivity: "trauma_witness",
          render: {
            kind: "solemn_archive_witness",
            visualSummary: "深色静态见证墙按北移、伏击围攻、损失和人物遭遇四段呈现。",
            interaction: {
              mode: "solemn_archive_witness",
              learnerAction: "逐段核对命令、人数和人物遭遇，阅读后进入政治反击文献节点。",
              feedback: "只作史实确认与统一战线影响归纳，不设置成败判定。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: traumaGuardrails,
          },
          reviewState: "pending_review",
          reviewNote:
            "历史照片和人物档案接入前须完成来源、权利与庄重呈现审核；当前建议以程序化见证墙承载。",
        },
        {
          id: "M3S4_N03",
          sceneId: "M3S4",
          order: 30,
          nodeType: "archive",
          title: "重建新四军军部与政治反击",
          timeAnchor: "1941年1月20日",
          estimatedSeconds: 40,
          claimId: "M3S4_CLAIM_REBUILD_NEW_FOURTH_ARMY",
          claim:
            "中央军委发布重建新四军军部命令，陈毅任代军长、刘少奇任政治委员；中国共产党同时在政治上坚决反击。",
          evidencePoints: [
            "面对严重形势，中国共产党仍以抗日大局为重，在军事上严守自卫，在政治上坚决反击。",
            "1941年1月20日，中央军委发布重建新四军军部命令，陈毅任代军长，刘少奇任政治委员。",
            "党中央公布大量事实揭露破坏抗战的阴谋，并提出惩办祸首、释放叶挺、废止国民党一党专政等12条解决办法。",
          ],
          verbatimQuotes: [
            {
              text: "为江南死国难者志哀",
              context: "《新华日报》刊出周恩来题词手迹之一",
              verbatim: true,
            },
            {
              text: "千古奇冤，江南一叶；同室操戈，相煎何急？！",
              context: "《新华日报》刊出周恩来题词手迹之二",
              verbatim: true,
            },
          ],
          assetSuggestions: [
            {
              id: "AST-M3S4-SUG-003",
              kind: "interface",
              label: "重建命令与《新华日报》文献研读墙",
              purpose: "并列重建命令、任命关系、12条办法和两条题词；不仿制档案原件。",
              representationLabel: "教材载述史料·非档案原件",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("90", "110"),
          sensitivity: "standard",
          render: {
            kind: "document_wall",
            visualSummary: "文献墙按军事重建与政治反击两条线并列，任命关系显著标注。",
            interaction: {
              mode: "document_evidence_reader",
              learnerAction: "核对命令日期、任命关系和题词出处，再进入正式任务T10。",
              feedback: "答错T10时返回“陈毅任代军长、刘少奇任政治委员”证据卡，不改变题目。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M3S4_N04",
          sceneId: "M3S4",
          order: 40,
          nodeType: "lecture",
          title: "第二次反共高潮被击退",
          timeAnchor: "1941年1—3月",
          estimatedSeconds: 25,
          claimId: "M3S4_CLAIM_SECOND_HIGH_TIDE_REPELLED",
          claim:
            "中国共产党的鲜明立场得到广泛同情支持，1941年3月国民党顽固派第二次反共高潮被击退。",
          evidencePoints: [
            "中国共产党以民族大义为重的立场，得到全国人民、中间势力、国民党内正义人士和国际舆论支持。",
            "宋庆龄、何香凝等在香港发起抗议运动，华侨领袖陈嘉庚致电国民参政会呼吁团结。",
            "1941年3月，蒋介石迫于压力公开“保证”决不再有“剿共”的军事行动。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M3S4-SUG-004",
              kind: "interface",
              label: "政治舆论反击响应网络",
              purpose: "呈现党报、全国人民、中间势力、爱国人士与国际舆论的响应关系。",
              representationLabel: "教材载述史料·非档案原件",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("90", "110"),
          sensitivity: "standard",
          render: {
            kind: "resistance_network",
            visualSummary: "政治舆论响应关系网围绕民族大义与团结抗战结论展开。",
            interaction: {
              mode: "evidence_matrix",
              learnerAction: "按群体打开响应证据，核对其共同支持的政治立场。",
              feedback: "说明政治反击如何维护抗日大局并击退第二次反共高潮。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
        {
          id: "M3S4_N05",
          sceneId: "M3S4",
          order: 50,
          nodeType: "lecture",
          title: "制止国民党第三次反共高潮",
          timeAnchor: "1943年春",
          estimatedSeconds: 35,
          claimId: "M3S4_CLAIM_THIRD_HIGH_TIDE_STOPPED",
          claim:
            "党在军事和政治上采取强有力反击措施，制止第三次反共高潮，展现驾驭复杂局面的成熟领导。",
          evidencePoints: [
            "1943年春，蒋介石署名出版反共反人民的《中国之命运》一书，暗示在两年内要消灭共产党和一切革命力量。接着，国民党顽固派又以当年5月共产国际宣布解散为由制造反共舆论并准备向陕甘宁边区进攻。",
            "党中央进行必要军事部署并采取政治上强有力的反击措施，使第三次反共高潮未发展成大规模武装进攻就被制止。",
            "党连续打退或制止三次反共高潮，既不惊慌失措、一味妥协，也不采取冒险行动给破坏团结抗日者以借口。",
            "许多中间人士由此看清共产党以民族利益为重，党在全国的政治地位空前提高。",
          ],
          verbatimQuotes: [],
          assetSuggestions: [
            {
              id: "AST-M3S4-SUG-005",
              kind: "programmatic_2_5d",
              label: "三次反共高潮与统一战线策略时间线",
              purpose: "以三段时间线比较党在复杂局面中的原则性与策略性。",
              representationLabel: "复原性创作·非档案影像",
              rightsPlan: "original_procedural",
            },
          ],
          source: textbookSource("90—91", "110—111"),
          sensitivity: "standard",
          render: {
            kind: "situation_timeline",
            visualSummary: "三次反共高潮时间线以局势、应对和结果三层证据收束模块论证。",
            interaction: {
              mode: "guided_timeline",
              learnerAction: "依次查看三次高潮的起因、应对和结果，并比较一致原则。",
              feedback: "归纳坚持抗战、团结、进步方针同中流砥柱作用的关系。",
              nonScored: true,
              historyConstraint: "只核验既定史实，不改变历史结果",
            },
            guardrails: standardGuardrails,
          },
          reviewState: "pending_review",
        },
      ],
      reviewState: "pending_review",
      reviewNotes: [
        {
          id: "M3S4_REVIEW_WANNAN_PRESENTATION",
          scope: "M3S4_N02",
          state: "open",
          note: "皖南事变必须以命令、时间、人数和纪念性文字作庄重史料见证；禁止伏击重演、伤亡特写、失败重来或战果积分。",
        },
      ],
    },
  ],
  reviewState: "pending_review",
  reviewNotes: [
    {
      id: "M3_REVIEW_PAGE_REFERENCE",
      scope: "M3全部节点",
      state: "open",
      note: "原始DeepSeek方案把源PDF第104—111页误称为书中页码；本蓝图按书内印刷84—91页 / 源PDF104—111页双字段记录，不改教材正文或正式任务。",
    },
    {
      id: "M3_REVIEW_DURATION_CONFLICT",
      scope: "M3标准路线",
      state: "open",
      note: "原方案模块总时长写约10分钟，但四场景知识讲授标注合计约12分钟；本蓝图暂保留逐场景180/240/120/180秒估算，待普通话旁白、交互与转场实测后再提交时长取舍，不通过删减史实或加速朗读解决。",
    },
  ],
} satisfies M3Blueprint;
